/*
 * Copyright © HatioLab Inc. All rights reserved.
 */

import {
  ComponentNature,
  RealObjectGroup,
  RealObject,
  RectPath,
  Shape,
  ValueHolder,
  sceneComponent
} from '@hatiolab/things-scene'
import * as THREE from 'three'

const NATURE: ComponentNature = {
  mutable: false,
  resizable: true,
  rotatable: true,
  properties: [
    {
      type: 'number',
      label: 'value',
      name: 'value',
      property: 'value'
    },
    {
      type: 'color',
      label: 'liquid-color',
      name: 'liquidColor',
      property: 'liquidColor'
    }
  ],
  help: 'scene/component/tank'
}

/* ── 상수 ── */
const TANK_COLOR = '#aaaaaa'
const LIQUID_COLOR = '#4488ff'
const LIQUID_OPACITY = 0.8

/* ── 게이지 ── */
const GAUGE_TEX_W = 64
const GAUGE_TEX_H = 256
const GAUGE_ARC = 0.22 // 라디안 (~13°) — 더 좁고 세련된 폭
const BEZEL_COLOR = '#2a2a2a'

/* ── 2D 렌더 ── */

function renderTank(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  value: number,
  liquidColor: string
) {
  const capH = Math.min(w * 0.25, h * 0.15)
  const bodyY = y + capH
  const bodyH = h - capH

  // 외벽 — 본체 (불투명)
  ctx.fillStyle = TANK_COLOR
  ctx.fillRect(x, bodyY, w, bodyH)

  // 외벽 — 상단 둥근 캡
  ctx.beginPath()
  ctx.ellipse(x + w / 2, bodyY, w / 2, capH, 0, Math.PI, 0)
  ctx.fill()

  // 외벽 윤곽선
  ctx.strokeStyle = '#888888'
  ctx.lineWidth = 1.5
  ctx.beginPath()
  ctx.moveTo(x, bodyY)
  ctx.lineTo(x, bodyY + bodyH)
  ctx.lineTo(x + w, bodyY + bodyH)
  ctx.lineTo(x + w, bodyY)
  ctx.stroke()
  ctx.beginPath()
  ctx.ellipse(x + w / 2, bodyY, w / 2, capH, 0, Math.PI, 0)
  ctx.stroke()

  // ── 전면 게이지 ──
  const gw = Math.max(6, w * 0.15)
  const gPad = bodyH * 0.15
  const gx = x + (w - gw) / 2
  const gy = bodyY + gPad
  const gh = bodyH - gPad * 2
  const gr = gw / 2

  // 액체 채움
  const level = Math.max(0, Math.min(100, value)) / 100
  if (level > 0) {
    const fp = 2
    const fillH = (gh - fp * 2) * level
    const fillY = gy + gh - fp - fillH

    ctx.save()
    ctx.beginPath()
    ctx.roundRect(gx + fp, gy + fp, gw - fp * 2, gh - fp * 2, Math.max(0, gr - fp))
    ctx.clip()

    ctx.fillStyle = liquidColor || LIQUID_COLOR
    ctx.globalAlpha = LIQUID_OPACITY
    ctx.fillRect(gx + fp, fillY, gw - fp * 2, fillH)
    ctx.globalAlpha = 1
    ctx.restore()
  }

  // 눈금 (우측, 20% 단위)
  ctx.strokeStyle = 'rgba(255,255,255,0.45)'
  const tickTop = gy + 4
  const tickAreaH = gh - 8
  for (let pct = 20; pct <= 80; pct += 20) {
    const ty = tickTop + tickAreaH * (1 - pct / 100)
    const tw = pct === 50 ? gw * 0.35 : gw * 0.2
    ctx.lineWidth = pct === 50 ? 1 : 0.5
    ctx.beginPath()
    ctx.moveTo(gx + gw - tw - 1, ty)
    ctx.lineTo(gx + gw - 1, ty)
    ctx.stroke()
  }

  // 게이지 테두리 (라운드)
  ctx.strokeStyle = 'rgba(255,255,255,0.2)'
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.roundRect(gx, gy, gw, gh, gr)
  ctx.stroke()

  // 수치 (게이지 중앙)
  const pctText = `${Math.round(value)}%`
  const fontSize = Math.max(7, Math.round(gw * 0.55))
  ctx.font = `bold ${fontSize}px sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.shadowColor = 'rgba(0,0,0,0.7)'
  ctx.shadowBlur = 2
  ctx.fillStyle = '#ffffff'
  ctx.fillText(pctText, gx + gw / 2, gy + gh / 2)
  ctx.shadowColor = 'transparent'
  ctx.shadowBlur = 0

  // bounds path 등록
  ctx.beginPath()
  ctx.rect(x, y, w, h)
}

/* ── 3D 렌더 ── */

export class Tank3D extends RealObjectGroup {
  private _gaugeCanvas!: HTMLCanvasElement
  private _gaugeCtx!: CanvasRenderingContext2D
  private _gaugeTexture!: THREE.CanvasTexture
  private _gaugeMesh!: THREE.Mesh

  // build 시 저장
  private _bodyRadius = 0
  private _bodyDepth = 0
  private _domeH = 0

  build() {
    super.build()

    const { width, height } = this.component.bounds
    const { depth } = this.component.state
    const d = depth || Math.max(width, height) * 2

    const radius = Math.min(width, height) / 2
    const domeH = radius * 0.5
    const bodyH = d - domeH

    this._bodyRadius = radius
    this._bodyDepth = bodyH
    this._domeH = domeH

    const gaugeH = bodyH * 0.55

    // ── 솔리드 탱크 본체 ──
    const tankMat = new THREE.MeshStandardMaterial({
      color: TANK_COLOR,
      roughness: 0.35,
      metalness: 0.5
    })

    // 원기둥 (닫힌 양 끝)
    const wallGeo = new THREE.CylinderGeometry(radius, radius, bodyH, 32)
    const wallMesh = new THREE.Mesh(wallGeo, tankMat)
    wallMesh.position.y = bodyH / 2
    wallMesh.castShadow = true
    this.object3d.add(wallMesh)

    // 상단 반구 돔
    const domeGeo = new THREE.SphereGeometry(radius, 32, 16, 0, Math.PI * 2, 0, Math.PI / 2)
    const domeMesh = new THREE.Mesh(domeGeo, tankMat)
    domeMesh.position.y = bodyH
    domeMesh.castShadow = true
    this.object3d.add(domeMesh)

    // ── 게이지 (곡면, 단일 메시 — 캔버스로 전부 그림) ──
    this._gaugeCanvas = document.createElement('canvas')
    this._gaugeCanvas.width = GAUGE_TEX_W
    this._gaugeCanvas.height = GAUGE_TEX_H
    this._gaugeCtx = this._gaugeCanvas.getContext('2d')!

    this._gaugeTexture = new THREE.CanvasTexture(this._gaugeCanvas)
    this._gaugeTexture.minFilter = THREE.LinearFilter

    const gaugeR = radius * 1.04
    const gaugeGeo = new THREE.CylinderGeometry(
      gaugeR, gaugeR, gaugeH, 24, 1, true,
      -GAUGE_ARC / 2, GAUGE_ARC
    )
    const gaugeMat = new THREE.MeshBasicMaterial({
      map: this._gaugeTexture,
      transparent: true,
      alphaTest: 0.05
    })
    this._gaugeMesh = new THREE.Mesh(gaugeGeo, gaugeMat)
    this._gaugeMesh.position.y = bodyH / 2
    this.object3d.add(this._gaugeMesh)

    // 게이지 그리기
    this._drawGauge()

    // 수직 중앙 정렬
    const totalH = bodyH + domeH
    this.object3d.children.forEach(child => {
      child.position.y -= totalH / 2
    })
  }

  private _drawGauge() {
    const ctx = this._gaugeCtx
    const w = GAUGE_TEX_W
    const h = GAUGE_TEX_H
    const comp = this.component as unknown as Tank
    const value = Math.max(0, Math.min(100, comp.value ?? 0))
    const level = value / 100
    const liquidColor = comp.getState('liquidColor') || LIQUID_COLOR

    // 레이아웃 — 캡슐 형태 유리 영역만
    const gx = 6
    const gy = 10
    const gw = w - gx * 2
    const gh = h - gy * 2
    const gr = gw / 2           // 캡슐 라운드

    ctx.clearRect(0, 0, w, h)

    // ── 빈 영역 (어두운 배경) ──
    ctx.fillStyle = 'rgba(15, 20, 25, 0.8)'
    ctx.beginPath()
    ctx.roundRect(gx, gy, gw, gh, gr)
    ctx.fill()

    // ── 액체 채움 ──
    if (level > 0) {
      const fillH = Math.round(gh * level)
      const fillY = gy + gh - fillH

      ctx.save()
      ctx.beginPath()
      ctx.roundRect(gx, gy, gw, gh, gr)
      ctx.clip()

      ctx.fillStyle = liquidColor
      ctx.globalAlpha = LIQUID_OPACITY
      ctx.fillRect(gx, fillY, gw, fillH)

      // 수면 하이라이트
      ctx.globalAlpha = 0.3
      const surfGrad = ctx.createLinearGradient(0, fillY, 0, fillY + 4)
      surfGrad.addColorStop(0, '#ffffff')
      surfGrad.addColorStop(1, 'transparent')
      ctx.fillStyle = surfGrad
      ctx.fillRect(gx, fillY, gw, 4)

      ctx.globalAlpha = 1
      ctx.restore()
    }

    // ── 눈금 (우측, 20% 단위) ──
    ctx.strokeStyle = 'rgba(255,255,255,0.4)'
    for (let pct = 20; pct <= 80; pct += 20) {
      const ty = gy + gh * (1 - pct / 100)
      const tw = pct === 50 ? gw * 0.35 : gw * 0.22
      ctx.lineWidth = pct === 50 ? 1.5 : 1
      ctx.beginPath()
      ctx.moveTo(gx + gw - tw, ty)
      ctx.lineTo(gx + gw, ty)
      ctx.stroke()
    }

    // ── 유리 반사 (좌측 하이라이트) ──
    ctx.save()
    ctx.beginPath()
    ctx.roundRect(gx, gy, gw, gh, gr)
    ctx.clip()
    const hlGrad = ctx.createLinearGradient(gx, 0, gx + gw, 0)
    hlGrad.addColorStop(0, 'rgba(255,255,255,0.12)')
    hlGrad.addColorStop(0.3, 'rgba(255,255,255,0.03)')
    hlGrad.addColorStop(1, 'rgba(255,255,255,0)')
    ctx.fillStyle = hlGrad
    ctx.fillRect(gx, gy, gw, gh)
    ctx.restore()

    // ── 유리 테두리 ──
    ctx.strokeStyle = 'rgba(255,255,255,0.15)'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.roundRect(gx, gy, gw, gh, gr)
    ctx.stroke()

    // ── 수치 (게이지 중앙) ──
    ctx.fillStyle = '#ffffff'
    ctx.font = 'bold 14px sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.shadowColor = 'rgba(0,0,0,0.7)'
    ctx.shadowBlur = 3
    ctx.fillText(`${Math.round(value)}%`, w / 2, gy + gh / 2)
    ctx.shadowColor = 'transparent'
    ctx.shadowBlur = 0

    this._gaugeTexture.needsUpdate = true
  }

  updateFillLevel() {
    this._drawGauge()
  }

  update() {
    super.update()
    this.updateFillLevel()
  }

  updateDimension() {
    this.clear()
    this.build()
  }

  onchange(after: Record<string, unknown>, before: Record<string, unknown>) {
    super.onchange(after, before)

    if ('value' in after || 'liquidColor' in after) {
      this.updateFillLevel()
    }
  }
}

/* ── Component 클래스 ── */

const MixedShape = ValueHolder(RectPath(Shape))

@sceneComponent('tank')
export class Tank extends MixedShape {
  is3dish() {
    return true
  }

  buildRealObject(): RealObject | undefined {
    return new Tank3D(this as any)
  }

  get nature() {
    return NATURE
  }

  onchange(after: Record<string, unknown>, before: Record<string, unknown>) {
    super.onchange(after, before)

    if ('value' in after || 'liquidColor' in after) {
      this.invalidate()
    }
  }

  render(ctx: CanvasRenderingContext2D) {
    const { left, top, width, height } = this.bounds

    renderTank(ctx, left, top, width, height, this.value, this.getState('liquidColor') || LIQUID_COLOR)

    ctx.beginPath()
    ctx.rect(left, top, width, height)
  }

}
